/**
 * Administração ▸ 📨 Caixa de entrada (27/10, pedido do Lucas).
 *
 * O que chegou em sistema@ademaldo.com.br: o ASO da ASMETRO, a nota de oficina
 * que alguém encaminhou. O sistema registra sozinho quando tudo fecha; o que
 * ficou com dúvida aparece em "Pendentes", com a dúvida escrita e os campos
 * para resolver e confirmar, ou descartar.
 */

import { api, ErroApi } from '../../api.js';
import { escapar, dataHora } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { TIPOS_DOCUMENTO } from '../sst_ficha.js';

type Filtro = 'pendente' | 'registrado' | 'duplicado' | 'descartado' | 'ignoradas' | 'parceiros';

interface Doc {
  id: number;
  tipo: 'aso' | 'nota_manutencao' | 'nota_material' | 'documento_funcionario' | 'outro';
  situacao: 'registrado' | 'pendente' | 'duplicado' | 'descartado';
  descricao: string | null;
  lido: Record<string, unknown> | null;
  duvidas: string[];
  sugestao: Record<string, unknown> | null;
  nome_arquivo: string | null;
  url: string | null;
  registro: string | null;
  automatico: boolean;
  resolvido_em: string | null;
  resolvido_por_nome: string | null;
  motivo_descarte: string | null;
  criado_em: string;
  remetente: string;
  remetente_nome: string | null;
  assunto: string | null;
  recebida_em: string;
  autenticado: boolean;
  origem: string;
}
interface Mensagem { id: number; recebida_em: string; remetente: string; remetente_nome: string | null; assunto: string | null; situacao: string; motivo: string | null; origem: string }
interface Parceiro { id: number; padrao: string; descricao: string }
interface Resposta {
  endereco: string;
  autorizada: boolean;
  teste: boolean;
  podeVerSaude: boolean;
  ultimaLeitura: { em: string; relatorio: { lidas?: number; registrados?: number; pendentes?: number; erros?: string[] } } | null;
  contagens: Record<'pendente' | 'registrado' | 'duplicado' | 'descartado' | 'ignoradas', number>;
  documentos: Doc[];
  mensagens: Mensagem[];
  parceiros: Parceiro[];
}
interface Veiculo { placa: string; descricao: string | null; ativo: boolean }

const NOME_TIPO: Record<Doc['tipo'], string> = {
  aso: '🩺 ASO',
  nota_manutencao: '🔧 Nota de oficina',
  nota_material: '🧱 Nota de material',
  documento_funcionario: '👷 Documento de funcionário',
  outro: '📄 Documento não reconhecido',
};
const TIPOS_EXAME: [string, string][] = [
  ['admissional', 'Admissional'], ['periodico', 'Periódico'], ['retorno', 'Retorno ao trabalho'],
  ['mudanca_risco', 'Mudança de risco'], ['demissional', 'Demissional'],
];
const APTIDOES: Record<string, string> = {
  altura: 'altura', espaco_confinado: 'espaço confinado', radiacao_ionizante: 'radiação', eletricidade: 'eletricidade',
  conducao_veiculo: 'direção de veículo', manipulacao_alimentos: 'alimentos',
};
const s = (v: unknown) => (v == null ? '' : String(v));
const br = (iso: unknown) => (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : s(iso));
const reais = (v: unknown) => (typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '');

export function montarCaixa(raiz: HTMLElement, aoMudar?: () => void): void {
  let filtro: Filtro = 'pendente';
  let veiculos: Veiculo[] | undefined;
  // O que a IA leu de cada documento, para os formulários de confirmação.
  const docPorId = new Map<number, Doc>();
  const lerDoc = (id: number): Doc | undefined => docPorId.get(id);

  const desenhar = async (): Promise<void> => {
    raiz.innerHTML = `<p class="carregando">Carregando a caixa de entrada…</p>`;
    let d: Resposta;
    try {
      d = await api.get<Resposta>(`/api/caixa?filtro=${filtro === 'parceiros' ? 'pendente' : filtro}`);
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(erro instanceof ErroApi ? erro.message : 'Não consegui carregar a caixa.')}</p>`;
      return;
    }
    docPorId.clear();
    for (const doc of d.documentos) docPorId.set(doc.id, doc);
    const ult = d.ultimaLeitura;
    const chip = (f: Filtro, nome: string, n?: number) =>
      `<button class="chip" type="button" data-cx-filtro="${f}" aria-pressed="${filtro === f}">${nome}${n !== undefined ? ` <span class="conta">${n}</span>` : ''}</button>`;

    raiz.innerHTML = `
      <section class="ped-painel">
        <header class="ficha-topo"><div>
          <h2>📨 Caixa de entrada</h2>
          <span class="sub-inline">Documentos mandados para <b>${escapar(d.endereco)}</b>: o sistema lê a cada 10 minutos, a IA diz o que é cada anexo e registra sozinho quando não há dúvida. O resto fica aqui para conferir.</span>
        </div></header>
        ${d.autorizada ? '' : `<p class="aviso">A caixa ainda não foi autorizada. No computador do Lucas: <code>python scripts/autorizar_caixa_sistema.py</code>, entrando com ${escapar(d.endereco)}.</p>`}
        ${d.teste ? '<p class="dica">🧪 Sandbox: a leitura aqui não marca nada no Gmail. O que for lido continua novo para a produção.</p>' : ''}
        <p class="dica">Última leitura: ${ult ? `<b>${dataHora(ult.em)}</b> · ${s(ult.relatorio.lidas ?? 0)} e-mail(s) novo(s), ${s(ult.relatorio.registrados ?? 0)} registrado(s), ${s(ult.relatorio.pendentes ?? 0)} pendente(s)${ult.relatorio.erros?.length ? ` · ⚠️ ${escapar(ult.relatorio.erros.join(' | '))}` : ''}` : '<b>nunca</b>'}</p>
        <div class="conf-acoes"><button class="btn" id="cx-ler" type="button" ${d.autorizada ? '' : 'disabled'}>📥 Ler agora</button>
          <span class="sub-inline">ASO só entra sozinho com CPF que casa com a folha; nota de oficina, com a placa do veículo na nota ou no assunto do e-mail.</span></div>
      </section>
      <div class="chips">
        ${chip('pendente', 'Pendentes', d.contagens.pendente)}
        ${chip('registrado', 'Registrados', d.contagens.registrado)}
        ${chip('duplicado', 'Repetidos', d.contagens.duplicado)}
        ${chip('descartado', 'Descartados', d.contagens.descartado)}
        ${chip('ignoradas', 'E-mails ignorados', d.contagens.ignoradas)}
        ${chip('parceiros', 'Quem pode mandar')}
      </div>
      <div id="cx-lista"></div>`;

    const lista = raiz.querySelector<HTMLElement>('#cx-lista')!;
    if (filtro === 'parceiros') desenharParceiros(lista, d.parceiros);
    else if (filtro === 'ignoradas') desenharIgnoradas(lista, d.mensagens);
    else lista.innerHTML = d.documentos.length ? d.documentos.map((doc) => cartao(doc, d.podeVerSaude)).join('') : `<p class="dica">${filtro === 'pendente' ? 'Nada para conferir. ✅' : 'Nada aqui.'}</p>`;

    raiz.querySelectorAll<HTMLButtonElement>('[data-cx-filtro]').forEach((b) =>
      b.addEventListener('click', () => {
        filtro = b.dataset['cxFiltro'] as Filtro;
        void desenhar();
      }),
    );
    raiz.querySelector<HTMLButtonElement>('#cx-ler')?.addEventListener('click', (ev) => {
      const b = ev.currentTarget as HTMLButtonElement;
      b.disabled = true;
      b.textContent = '⏳ Lendo a caixa… (até 1 min)';
      void api
        .post<{ lidas: number; registrados: number; pendentes: number; restam: boolean }>('/api/caixa/ler', {})
        .then((r) => {
          avisar(`${r.lidas} e-mail(s) novo(s): ${r.registrados} registrado(s), ${r.pendentes} pendente(s)${r.restam ? '. Ainda há e-mail para a próxima leitura.' : '.'}`, 'ok');
          aoMudar?.();
          return desenhar();
        })
        .catch((e) => {
          b.disabled = false;
          b.textContent = '📥 Ler agora';
          avisarBloqueante(e instanceof ErroApi ? e.message : 'A leitura falhou.');
        });
    });
    if (filtro === 'pendente') ligarPendentes(lista);
  };

  // ------------------------------------------------------------ cartões

  const resumoLido = (doc: Doc): string => {
    const l = doc.lido;
    if (!l) return '';
    if (doc.tipo === 'aso') {
      const apt = Object.entries((l['aptidoes'] as Record<string, string> | undefined) ?? {})
        .filter(([, v]) => v === 'apto' || v === 'inapto' || v === 'ilegivel')
        .map(([k, v]) => `${v === 'apto' ? '✅' : v === 'inapto' ? '⛔' : '❓'} ${APTIDOES[k] ?? k}`);
      const exames = (l['exames'] as unknown[] | undefined)?.length ?? 0;
      return `<dl class="cx-lido">
        <dt>Funcionário</dt><dd>${escapar(s(l['nome']))} · CPF ${escapar(s(l['cpf']))}${l['nascimento'] ? ` · nasc. ${br(l['nascimento'])}` : ''}</dd>
        <dt>Exame</dt><dd>${escapar(s(l['tipoExame']))} em ${br(l['dataExame'])}${l['validade'] ? ` · vale até ${br(l['validade'])}` : ''} · ${exames} exame(s) no ASO</dd>
        <dt>Conclusão</dt><dd><b>${escapar(s(l['conclusao']) || '—')}</b>${apt.length ? ` · ${apt.join(' · ')}` : ''}</dd>
      </dl>`;
    }
    if (doc.tipo === 'documento_funcionario') {
      const nomeTipo = TIPOS_DOCUMENTO.find(([v]) => v === s(l['subtipo']))?.[1] ?? s(l['subtipo']);
      return `<dl class="cx-lido">
        <dt>Funcionário</dt><dd>${escapar(s(l['pessoa']) || '—')}${l['cpf'] ? ` · CPF ${escapar(s(l['cpf']))}` : ' · <b>sem CPF no documento</b>'}</dd>
        <dt>Documento</dt><dd>${escapar(nomeTipo || '—')}${l['data'] ? ` · ${br(l['data'])}` : ''}</dd>
        <dt>Resumo</dt><dd>${escapar(s(l['descricao']) || '—')}</dd>
      </dl>`;
    }
    if (doc.tipo === 'nota_manutencao') {
      return `<dl class="cx-lido">
        <dt>Oficina</dt><dd>${escapar(s(l['fornecedor']))}${l['numero'] ? ` · nota ${escapar(s(l['numero']))}` : ''}${l['emitidaEm'] ? ` · ${br(l['emitidaEm'])}` : ''}</dd>
        <dt>Valor</dt><dd>${reais(l['valorTotal'])}${l['placa'] ? ` · placa ${escapar(s(l['placa']))}` : ''}${l['km'] ? ` · ${s(l['km'])} km` : ''}</dd>
        <dt>O que foi</dt><dd>${escapar(s(l['resumo']) || s(l['tipoSugerido']))}</dd>
      </dl>`;
    }
    return '';
  };

  const cartao = (doc: Doc, podeVerSaude: boolean): string => {
    const de = `${escapar(doc.remetente_nome ?? doc.remetente)}${doc.remetente_nome ? ` &lt;${escapar(doc.remetente)}&gt;` : ''}`;
    const estado =
      doc.situacao === 'registrado'
        ? `✅ ${escapar(doc.registro ?? 'registrado')} · ${doc.automatico ? 'sozinho' : `confirmado por ${escapar(doc.resolvido_por_nome ?? '—')}`}`
        : doc.situacao === 'duplicado'
          ? `↩️ já estava no sistema${doc.registro ? `: ${escapar(doc.registro)}` : ''}`
          : doc.situacao === 'descartado'
            ? `🗑️ descartado por ${escapar(doc.resolvido_por_nome ?? '—')}${doc.motivo_descarte ? `: ${escapar(doc.motivo_descarte)}` : ''}`
            : '⏳ pendente';
    // O ASO e o dossiê do funcionário só abrem com o módulo SST (03/11).
    const saudeOculta = (doc.tipo === 'aso' || doc.tipo === 'documento_funcionario') && !podeVerSaude;
    return `
      <article class="vg-exec cx-doc ${doc.situacao === 'pendente' ? 'vg-alerta' : ''}" data-cx-doc="${doc.id}" data-cx-tipo="${doc.tipo}">
        <header class="cx-cab">
          <b>${NOME_TIPO[doc.tipo]}</b>
          <span class="sub-inline">${escapar(doc.descricao ?? doc.nome_arquivo ?? '')}</span>
        </header>
        <p class="sub-inline">De ${de} · “${escapar(doc.assunto ?? '')}” · ${dataHora(doc.recebida_em)}${doc.autenticado ? '' : ' · <span class="etiqueta">remetente não conferido</span>'}</p>
        <p class="sub-inline">${estado}${doc.url ? ` · <a href="${escapar(api.foto(doc.url) ?? '')}" target="_blank" rel="noopener">📄 abrir o arquivo</a>` : ''}</p>
        ${doc.duvidas?.length && doc.situacao === 'pendente' ? `<ul class="cx-duvidas">${doc.duvidas.map((x) => `<li>⚠️ ${escapar(x)}</li>`).join('')}</ul>` : ''}
        ${saudeOculta ? '' : resumoLido(doc)}
        ${doc.situacao === 'pendente' && !saudeOculta ? `<div class="cx-form" data-cx-form="${doc.id}"></div>` : ''}
        ${doc.situacao === 'pendente' && saudeOculta ? `<p class="dica">Para conferir este ${doc.tipo === 'aso' ? 'ASO' : 'documento de funcionário'} é preciso também o módulo Segurança do Trabalho.</p>` : ''}
      </article>`;
  };

  // -------------------------------------------------- resolver pendentes

  function ligarPendentes(lista: HTMLElement) {
    lista.querySelectorAll<HTMLElement>('[data-cx-form]').forEach((caixaForm) => {
      const art = caixaForm.closest<HTMLElement>('[data-cx-doc]')!;
      const id = Number(art.dataset['cxDoc']);
      const tipo = art.dataset['cxTipo'] as Doc['tipo'];
      if (tipo === 'aso') return void formAso(caixaForm, id);
      if (tipo === 'documento_funcionario') return void formDocumentoFuncionario(caixaForm, id);
      if (tipo === 'nota_manutencao') return void formManutencao(caixaForm, id);
      caixaForm.innerHTML = `
        <div class="conf-acoes">
          <button class="btn btn-claro" type="button" data-cx-reler="aso">🩺 Ler como ASO</button>
          <button class="btn btn-claro" type="button" data-cx-reler="documento_funcionario">👷 Ler como documento de funcionário</button>
          <button class="btn btn-claro" type="button" data-cx-reler="nota_manutencao">🔧 Ler como nota de oficina</button>
          <button class="btn btn-claro" type="button" data-cx-descartar>🗑️ Descartar</button>
        </div>`;
      ligarComuns(caixaForm, id);
    });
  }

  function ligarComuns(form: HTMLElement, id: number) {
    form.querySelectorAll<HTMLButtonElement>('[data-cx-reler]').forEach((b) =>
      b.addEventListener('click', () => {
        b.disabled = true;
        b.textContent = '⏳ Lendo…';
        void api
          .post<{ situacao: string }>(`/api/caixa/documentos/${id}/reler`, { como: b.dataset['cxReler'] })
          .then((r) => {
            avisar(r.situacao === 'registrado' ? 'Lido e registrado.' : 'Lido de novo: confira os campos.', 'ok');
            aoMudar?.();
            return desenhar();
          })
          .catch((e) => {
            b.disabled = false;
            avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui ler de novo.');
          });
      }),
    );
    form.querySelector<HTMLButtonElement>('[data-cx-descartar]')?.addEventListener('click', () => {
      const motivo = window.prompt('Por que descartar? (opcional)') ?? undefined;
      void api
        .post(`/api/caixa/documentos/${id}/descartar`, motivo ? { motivo } : {})
        .then(() => {
          avisar('Descartado.', 'ok');
          aoMudar?.();
          return desenhar();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui descartar.'));
    });
  }

  function formAso(form: HTMLElement, id: number) {
    const doc = lerDoc(id);
    const l = doc?.lido ?? {};
    const sug = doc?.sugestao ?? {};
    const conclusao = s(l['conclusao']);
    form.innerHTML = `
      <div class="cx-campos">
        <label>Funcionário
          <input class="campo" type="search" data-cx-busca placeholder="Digite o nome…" value="${escapar(s(sug['funcionarioNome']))}">
          <input type="hidden" data-cx-func value="${escapar(s(sug['funcionarioId']))}">
          <span class="sub-inline" data-cx-escolhido>${sug['funcionarioNome'] ? `sugerido: ${escapar(s(sug['funcionarioNome']))}` : 'busque pelo nome e escolha'}</span>
          <div class="cx-opcoes" data-cx-opcoes></div>
        </label>
        <label>Tipo de exame
          <select class="campo" data-cx-tipo-exame>${TIPOS_EXAME.map(([v, n]) => `<option value="${v}" ${s(l['tipoExame']) === v ? 'selected' : ''}>${n}</option>`).join('')}</select>
        </label>
        <label>Data do exame <input class="campo" type="date" data-cx-data value="${escapar(s(l['dataExame']))}"></label>
        <label>Validade <input class="campo" type="date" data-cx-validade value="${escapar(s(l['validade']))}"></label>
        <label>Resultado
          <select class="campo" data-cx-resultado>
            <option value="apto" ${conclusao === 'apto' ? 'selected' : ''}>Apto</option>
            <option value="apto_restricao" ${conclusao === 'apto_restricao' ? 'selected' : ''}>Apto com restrição</option>
            <option value="inapto" ${conclusao === 'inapto' ? 'selected' : ''}>Inapto</option>
          </select>
        </label>
      </div>
      <div class="conf-acoes">
        <button class="btn" type="button" data-cx-confirmar>✓ Confirmar e registrar</button>
        <button class="btn btn-claro" type="button" data-cx-descartar>🗑️ Descartar</button>
      </div>`;
    const func = ligarBuscaFuncionario(form);
    form.querySelector<HTMLButtonElement>('[data-cx-confirmar]')!.addEventListener('click', (ev) => {
      const b = ev.currentTarget as HTMLButtonElement;
      const funcionarioId = Number(func.value);
      if (!funcionarioId) return avisarBloqueante('Escolha o funcionário.');
      const corpo = {
        funcionarioId,
        tipo: form.querySelector<HTMLSelectElement>('[data-cx-tipo-exame]')!.value,
        dataExame: form.querySelector<HTMLInputElement>('[data-cx-data]')!.value,
        validade: form.querySelector<HTMLInputElement>('[data-cx-validade]')!.value || null,
        resultado: form.querySelector<HTMLSelectElement>('[data-cx-resultado]')!.value,
      };
      confirmar(b, id, corpo);
    });
    ligarComuns(form, id);
  }

  /** O campo "Funcionário" dos formulários de ASO e de documento: busca no cadastro da folha e escolhe. Devolve o campo escondido com o id. */
  function ligarBuscaFuncionario(form: HTMLElement): HTMLInputElement {
    const busca = form.querySelector<HTMLInputElement>('[data-cx-busca]')!;
    const opcoes = form.querySelector<HTMLElement>('[data-cx-opcoes]')!;
    const escolhido = form.querySelector<HTMLElement>('[data-cx-escolhido]')!;
    const func = form.querySelector<HTMLInputElement>('[data-cx-func]')!;
    let espera: number | undefined;
    busca.addEventListener('input', () => {
      window.clearTimeout(espera);
      func.value = '';
      escolhido.textContent = 'busque pelo nome e escolha';
      const t = busca.value.trim();
      if (t.length < 2) return void (opcoes.innerHTML = '');
      espera = window.setTimeout(() => {
        void api
          .get<{ funcionarios: { id: number; nome: string; nascimento: string | null; funcao: string | null; desligamento: string | null }[] }>(`/api/sst/funcionarios?q=${encodeURIComponent(t)}`)
          .then((r) => {
            opcoes.innerHTML = r.funcionarios
              .map((f) => `<button class="chip" type="button" data-cx-pega="${f.id}" data-nome="${escapar(f.nome)}">${escapar(f.nome)}<span class="conta">${f.nascimento ? ` · ${br(f.nascimento)}` : ''}${f.funcao ? ` · ${escapar(f.funcao)}` : ''}${f.desligamento ? ' · desligado' : ''}</span></button>`)
              .join('') || '<span class="sub-inline">ninguém com esse nome no cadastro da folha</span>';
            opcoes.querySelectorAll<HTMLButtonElement>('[data-cx-pega]').forEach((b) =>
              b.addEventListener('click', () => {
                func.value = b.dataset['cxPega']!;
                busca.value = b.dataset['nome']!;
                escolhido.textContent = `escolhido: ${b.dataset['nome']}`;
                opcoes.innerHTML = '';
              }),
            );
          })
          .catch(() => (opcoes.innerHTML = '<span class="sub-inline">não consegui buscar</span>'));
      }, 300);
    });
    return func;
  }

  /** Documento de funcionário que não é ASO (sst/008): vai para a pasta dele, com tipo e data. */
  function formDocumentoFuncionario(form: HTMLElement, id: number) {
    const doc = lerDoc(id);
    const sug = doc?.sugestao ?? {};
    const tipoSug = s(sug['tipo']);
    form.innerHTML = `
      <div class="cx-campos">
        <label>Funcionário
          <input class="campo" type="search" data-cx-busca placeholder="Digite o nome…" value="${escapar(s(sug['funcionarioNome']))}">
          <input type="hidden" data-cx-func value="${escapar(s(sug['funcionarioId']))}">
          <span class="sub-inline" data-cx-escolhido>${sug['funcionarioNome'] ? `sugerido: ${escapar(s(sug['funcionarioNome']))}` : 'busque pelo nome e escolha'}</span>
          <div class="cx-opcoes" data-cx-opcoes></div>
        </label>
        <label>Tipo de documento
          <select class="campo" data-cx-tipo-doc>${TIPOS_DOCUMENTO.map(([v, n]) => `<option value="${v}" ${tipoSug === v ? 'selected' : ''}>${escapar(n)}</option>`).join('')}</select>
        </label>
        <label>Título <input class="campo" data-cx-titulo maxlength="160" value="${escapar(s(sug['titulo']))}"></label>
        <label>Data do documento <input class="campo" type="date" data-cx-data value="${escapar(s(sug['data']))}"></label>
      </div>
      <div class="conf-acoes">
        <button class="btn" type="button" data-cx-confirmar>✓ Guardar na pasta do funcionário</button>
        <button class="btn btn-claro" type="button" data-cx-reler="aso">🩺 Ler como ASO</button>
        <button class="btn btn-claro" type="button" data-cx-descartar>🗑️ Descartar</button>
      </div>`;
    const func = ligarBuscaFuncionario(form);
    form.querySelector<HTMLButtonElement>('[data-cx-confirmar]')!.addEventListener('click', (ev) => {
      const b = ev.currentTarget as HTMLButtonElement;
      const funcionarioId = Number(func.value);
      if (!funcionarioId) return avisarBloqueante('Escolha o funcionário.');
      confirmar(b, id, {
        funcionarioId,
        tipo: form.querySelector<HTMLSelectElement>('[data-cx-tipo-doc]')!.value,
        titulo: form.querySelector<HTMLInputElement>('[data-cx-titulo]')!.value.trim() || null,
        data: form.querySelector<HTMLInputElement>('[data-cx-data]')!.value || null,
      });
    });
    ligarComuns(form, id);
  }

  function formManutencao(form: HTMLElement, id: number) {
    const doc = lerDoc(id);
    const l = doc?.lido ?? {};
    const sug = doc?.sugestao ?? {};
    const placaSug = s(sug['placa'] ?? l['placa']).toUpperCase();
    form.innerHTML = `
      <div class="cx-campos">
        <label>Veículo <select class="campo" data-cx-placa><option value="">carregando…</option></select></label>
        <label>Tipo <input class="campo" data-cx-tipo-man value="${escapar(s(l['tipoSugerido']) || 'manutenção')}"></label>
        <label>Descrição <input class="campo" data-cx-desc value="${escapar(s(l['resumo']))}"></label>
        <label>Valor (R$) <input class="campo" type="number" step="0.01" min="0" data-cx-valor value="${escapar(s(l['valorTotal']))}"></label>
        <label>Km <input class="campo" type="number" min="0" step="1" data-cx-km value="${escapar(s(l['km']))}"></label>
        <label>Data <input class="campo" type="date" data-cx-data value="${escapar(s(l['emitidaEm']))}"></label>
      </div>
      <div class="conf-acoes">
        <button class="btn" type="button" data-cx-confirmar>✓ Confirmar e lançar</button>
        <button class="btn btn-claro" type="button" data-cx-descartar>🗑️ Descartar</button>
      </div>`;
    const sel = form.querySelector<HTMLSelectElement>('[data-cx-placa]')!;
    const preencher = (vs: Veiculo[]) => {
      sel.innerHTML = `<option value="">— escolha —</option>` +
        vs.filter((v) => v.ativo !== false).map((v) => `<option value="${escapar(v.placa)}" ${v.placa === placaSug ? 'selected' : ''}>${escapar(v.placa)}${v.descricao ? ` · ${escapar(v.descricao)}` : ''}</option>`).join('');
    };
    if (veiculos) preencher(veiculos);
    else void api.get<Veiculo[] | { veiculos: Veiculo[] }>('/api/frota/veiculos').then((r) => {
      veiculos = Array.isArray(r) ? r : r.veiculos;
      preencher(veiculos);
    }).catch(() => (sel.innerHTML = '<option value="">não consegui carregar</option>'));

    form.querySelector<HTMLButtonElement>('[data-cx-confirmar]')!.addEventListener('click', (ev) => {
      const b = ev.currentTarget as HTMLButtonElement;
      if (!sel.value) return avisarBloqueante('Escolha o veículo.');
      const num = (q: string) => {
        const v = form.querySelector<HTMLInputElement>(q)!.value;
        return v === '' ? undefined : Number(v);
      };
      const corpo: Record<string, unknown> = {
        placa: sel.value,
        tipo: form.querySelector<HTMLInputElement>('[data-cx-tipo-man]')!.value.trim() || 'manutenção',
        descricao: form.querySelector<HTMLInputElement>('[data-cx-desc]')!.value.trim() || undefined,
        valor: num('[data-cx-valor]'),
        km: num('[data-cx-km]'),
        ocorridoEm: form.querySelector<HTMLInputElement>('[data-cx-data]')!.value || undefined,
      };
      confirmar(b, id, corpo);
    });
    ligarComuns(form, id);
  }

  function confirmar(b: HTMLButtonElement, id: number, corpo: Record<string, unknown>) {
    b.disabled = true;
    const texto = b.textContent;
    b.textContent = '⏳ Registrando…';
    void api
      .post<{ situacao: string }>(`/api/caixa/documentos/${id}/confirmar`, corpo)
      .then(() => {
        avisar('Registrado.', 'ok');
        aoMudar?.();
        return desenhar();
      })
      .catch((e) => {
        b.disabled = false;
        b.textContent = texto;
        avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui registrar.');
      });
  }

  // ------------------------------------------------- ignorados e parceiros

  function desenharIgnoradas(lista: HTMLElement, msgs: Mensagem[]) {
    if (!msgs.length) {
      lista.innerHTML = '<p class="dica">Nenhum e-mail ignorado.</p>';
      return;
    }
    lista.innerHTML = `
      <p class="dica">E-mails que o sistema não leu: remetente fora do cadastro e dos parceiros, sem anexo, ou com erro. "Processar mesmo assim" lê os anexos, mas nada entra sem alguém confirmar.</p>
      <table class="tabela"><thead><tr><th>Quando</th><th>De</th><th>Assunto</th><th>Motivo</th><th></th></tr></thead><tbody>
      ${msgs
        .map(
          (m) => `<tr>
            <td>${dataHora(m.recebida_em)}</td>
            <td>${escapar(m.remetente_nome ?? m.remetente)}<br><span class="sub-inline">${escapar(m.remetente)}</span></td>
            <td>${escapar(m.assunto ?? '')}</td>
            <td>${m.situacao === 'erro' ? '⚠️ ' : ''}${escapar(m.motivo ?? '')}</td>
            <td><button class="btn btn-claro" type="button" data-cx-processar="${m.id}">Processar mesmo assim</button></td>
          </tr>`,
        )
        .join('')}
      </tbody></table>`;
    lista.querySelectorAll<HTMLButtonElement>('[data-cx-processar]').forEach((b) =>
      b.addEventListener('click', () => {
        b.disabled = true;
        b.textContent = '⏳ Processando…';
        void api
          .post<{ registrados: number; pendentes: number }>(`/api/caixa/mensagens/${b.dataset['cxProcessar']}/processar`, {})
          .then((r) => {
            avisar(`Processado: ${r.pendentes} documento(s) para conferir.`, 'ok');
            aoMudar?.();
            filtro = 'pendente';
            return desenhar();
          })
          .catch((e) => {
            b.disabled = false;
            b.textContent = 'Processar mesmo assim';
            avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui processar.');
          });
      }),
    );
  }

  function desenharParceiros(lista: HTMLElement, parceiros: Parceiro[]) {
    lista.innerHTML = `
      <p class="dica">Quem está no cadastro de Pessoas com e-mail já pode mandar documento. Aqui entram os de fora: um domínio inteiro (asmetro.com.br) ou um endereço só.</p>
      <table class="tabela"><thead><tr><th>Domínio ou e-mail</th><th>Quem é</th><th></th></tr></thead><tbody>
        ${parceiros.map((p) => `<tr><td><b>${escapar(p.padrao)}</b></td><td>${escapar(p.descricao)}</td><td><button class="btn btn-claro" type="button" data-cx-tirar="${p.id}">Tirar</button></td></tr>`).join('')}
      </tbody></table>
      <div class="cx-campos">
        <label>Domínio ou e-mail <input class="campo" id="cx-padrao" placeholder="oficina.com.br"></label>
        <label>Quem é <input class="campo" id="cx-descr" placeholder="Auto Mecânica Silva (notas de serviço)"></label>
      </div>
      <div class="conf-acoes"><button class="btn" type="button" id="cx-incluir">+ Liberar</button></div>`;
    lista.querySelector<HTMLButtonElement>('#cx-incluir')!.addEventListener('click', () => {
      const padrao = lista.querySelector<HTMLInputElement>('#cx-padrao')!.value.trim();
      const descricao = lista.querySelector<HTMLInputElement>('#cx-descr')!.value.trim();
      void api
        .post('/api/caixa/parceiros', { padrao, descricao })
        .then(() => {
          avisar('Liberado.', 'ok');
          return desenhar();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui liberar.'));
    });
    lista.querySelectorAll<HTMLButtonElement>('[data-cx-tirar]').forEach((b) =>
      b.addEventListener('click', () => {
        void api
          .del(`/api/caixa/parceiros/${b.dataset['cxTirar']}`)
          .then(() => desenhar())
          .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui tirar.'));
      }),
    );
  }

  void desenhar();
}
